<!-- src/components/CaseDetail.vue -->
<!--
  Case Detail view. This is where the agent does most of their work
  on an individual case.

  First-pass scope:
    - Sticky header (Case ID, defendant, status, primary actions)
    - Participants section (cards for defendant + indemnitor(s))
    - Stub placeholders for Case Fields / Documents / Notes / Activity

  Communication:
    - Receives `caseData` as a prop
    - Emits 'back' when the breadcrumb is clicked
    - Emits 'update' with a mutated case object when something changes
      (stub handlers for now)

  This view doesn't fetch or persist anything — the parent (Forms.vue)
  passes data in and listens for updates. Keeps it easy to swap the
  data layer later.
-->

<template>
  <div class="case-detail">

    <!-- ───── Sticky header ───── -->
    <div class="case-header">
      <div class="case-header-inner">

        <div class="case-header-left">
          <v-btn
            variant="text"
            density="comfortable"
            class="back-btn"
            @click="$emit('back')"
          >
            <v-icon start>mdi-arrow-left</v-icon>
            Cases
          </v-btn>

          <div class="case-header-divider"></div>

          <div class="case-header-info">
            <div class="case-header-title">
              <span class="case-id" :class="{ 'case-id-missing': !caseDisplayId }">
                {{ caseDisplayId || 'No Case ID' }}
              </span>
              <span class="case-header-sep">·</span>
              <span class="case-defendant">
                {{ displayName(defendant) || 'No defendant name' }}
              </span>
              <span v-if="bondAmount" class="case-header-sep">·</span>
              <span v-if="bondAmount" class="case-bond">
                {{ formatCurrency(bondAmount) }}
              </span>
            </div>
            <div class="case-header-subtitle">
              <span v-if="court">
                {{ court }}
              </span>
              <span v-if="court" class="case-header-sep">·</span>
              <span>Created {{ formatTimeAgo(caseData.created_at) }}</span>
            </div>
          </div>
        </div>

        <div class="case-header-right">
          <v-chip
            :color="derivedStatus.color"
            size="small"
            variant="flat"
            class="header-status-chip"
          >
            {{ derivedStatus.label }}
          </v-chip>

          <v-menu>
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                color="#635347"
                variant="flat"
                size="small"
              >
                Actions
                <v-icon end>mdi-menu-down</v-icon>
              </v-btn>
            </template>
            <v-list density="compact">
              <v-list-item @click="onAction('send_all_links')">
                <template #prepend>
                  <v-icon size="18">mdi-email-send-outline</v-icon>
                </template>
                <v-list-item-title>Send all magic links</v-list-item-title>
              </v-list-item>
              <v-list-item @click="onAction('generate_documents')">
                <template #prepend>
                  <v-icon size="18">mdi-file-document-outline</v-icon>
                </template>
                <v-list-item-title>Generate documents</v-list-item-title>
              </v-list-item>
              <v-list-item @click="onAction('seal_documents')">
                <template #prepend>
                  <v-icon size="18">mdi-lock-check-outline</v-icon>
                </template>
                <v-list-item-title>Seal completed documents</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <v-menu>
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                variant="text"
                size="small"
                icon
              >
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list density="compact">
              <v-list-item @click="onAction('archive')">
                <template #prepend>
                  <v-icon size="18">mdi-archive-outline</v-icon>
                </template>
                <v-list-item-title>Archive case</v-list-item-title>
              </v-list-item>
              <v-list-item @click="onAction('delete')">
                <template #prepend>
                  <v-icon size="18" color="red-darken-1">mdi-delete-outline</v-icon>
                </template>
                <v-list-item-title class="text-red-darken-1">
                  Delete case
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>

      </div>
    </div>

    <!-- ───── Main scrollable body ───── -->
    <div class="case-body">

      <!-- ───── Section: Participants ───── -->
      <section class="case-section">
        <div class="section-header">
          <h2 class="section-title">Participants</h2>
          <v-btn
            v-if="indemnitors.length < 2"
            variant="text"
            size="small"
            color="#635347"
            @click="onAction('add_indemnitor')"
          >
            <v-icon start size="16">mdi-plus</v-icon>
            Add indemnitor
          </v-btn>
        </div>

        <!-- Defendant card -->
        <ParticipantCard
          :participant="defendant"
          role="defendant"
          @send-link="onAction('send_link', { role: 'defendant' })"
          @view-form="onAction('view_form', { role: 'defendant' })"
          @ok-to-sign="onAction('ok_to_sign', { role: 'defendant' })"
          @view-docs="onAction('view_docs', { role: 'defendant' })"
        />

        <!-- Indemnitor card(s) -->
        <ParticipantCard
          v-for="(indem, idx) in indemnitors"
          :key="'indem-' + idx"
          :participant="indem"
          role="indemnitor"
          :role-suffix="indemnitors.length > 1 ? ` ${idx + 1}` : ''"
          @send-link="onAction('send_link', { role: 'indemnitor', idx })"
          @view-form="onAction('view_form', { role: 'indemnitor', idx })"
          @ok-to-sign="onAction('ok_to_sign', { role: 'indemnitor', idx })"
          @view-docs="onAction('view_docs', { role: 'indemnitor', idx })"
          @remove="onAction('remove_indemnitor', { idx })"
        />

        <!-- Empty state: no indemnitors yet -->
        <div v-if="indemnitors.length === 0" class="empty-indemnitor">
          <v-icon size="36" color="grey-lighten-1">mdi-shield-account-outline</v-icon>
          <div class="empty-indemnitor-text">
            No indemnitor yet.
            <v-btn
              variant="text"
              size="small"
              color="#635347"
              @click="onAction('add_indemnitor')"
            >
              Add one
            </v-btn>
          </div>
        </div>
      </section>

      <!-- ───── Section: Case Fields (stub) ───── -->
      <section class="case-section">
        <h2 class="section-title">Case Fields</h2>
        <div class="section-stub">
          <v-icon size="20" color="grey-lighten-1">mdi-information-outline</v-icon>
          <span>Case Fields editor coming in the next pass. Court name,
            bond amount, POA, charges, etc.</span>
        </div>
      </section>

      <!-- ───── Section: Documents (stub) ───── -->
      <section class="case-section">
        <h2 class="section-title">Documents</h2>
        <div class="section-stub">
          <v-icon size="20" color="grey-lighten-1">mdi-information-outline</v-icon>
          <span>Document selection &amp; generation status coming in a
            later pass.</span>
        </div>
      </section>

      <!-- ───── Section: Notes (stub) ───── -->
      <section class="case-section">
        <h2 class="section-title">Notes</h2>
        <div v-if="caseData.notes" class="notes-display">
          {{ caseData.notes }}
        </div>
        <div v-else class="section-stub">
          <v-icon size="20" color="grey-lighten-1">mdi-note-text-outline</v-icon>
          <span>No notes yet.</span>
        </div>
      </section>

    </div>

  </div>
</template>


<script>
import ParticipantCard from './ParticipantCard.vue';

const HOUR = 1000 * 60 * 60;
const DAY = HOUR * 24;

// Same status-derivation logic as Forms.vue. In a later pass we should
// move this into a shared util so the dashboard and the case detail
// view both call the same function. For now it's duplicated.
function _allParticipants(c) {
  const d = c.participants?.defendant;
  const i1 = c.participants?.indemnitor1;
  const i2 = c.participants?.indemnitor2;
  return [d, i1, i2].filter(Boolean);
}

function derivedStatusFor(c) {
  switch (c.status) {
    case 'draft': {
      const d = c.participants?.defendant;
      const missing = [];
      if (!c.case_fields?.court_case_number) missing.push('Case #');
      if (!d?.identity?.first_name && !d?.identity?.last_name) missing.push('defendant name');
      if (!c.case_fields?.bond_amount_numeric) missing.push('bond amount');
      if (missing.length > 0) {
        return {
          label: 'Draft',
          color: 'grey',
          detail: `Missing: ${missing.slice(0, 2).join(', ')}` +
                  (missing.length > 2 ? '…' : ''),
        };
      }
      return { label: 'Ready to invite', color: 'amber-darken-2' };
    }
    case 'active': {
      const all = _allParticipants(c);
      if (all.length === 0) return { label: 'Active', color: 'blue' };
      const statuses = all.map(p => p.status);
      if (statuses.every(s => s === 'awaiting_invite'))
        return { label: 'Ready to invite', color: 'amber-darken-2' };
      if (statuses.every(s => s === 'submitted'))
        return { label: 'Ready for review', color: 'amber-darken-2' };
      if (statuses.every(s => s === 'signed' || s === 'ready_to_sign'))
        return { label: 'Ready to seal', color: 'amber-darken-2' };
      if (statuses.some(s => s === 'filling' || s === 'invited'))
        return { label: 'Forms in progress', color: 'blue' };
      return { label: 'Active', color: 'blue' };
    }
    case 'completed':
      return { label: c.sealed ? 'Sealed' : 'Completed', color: 'green-darken-2' };
    case 'archived':
      return { label: 'Archived', color: 'green-darken-2' };
    default:
      return { label: c.status || '—', color: 'grey' };
  }
}

export default {
  name: 'CaseDetail',

  components: { ParticipantCard },

  props: {
    caseData: { type: Object, required: true },
  },

  emits: ['back', 'update'],

  computed: {
    derivedStatus() {
      return derivedStatusFor(this.caseData);
    },
    defendant() {
      return this.caseData.participants?.defendant || null;
    },
    indemnitors() {
      const p = this.caseData.participants || {};
      return [p.indemnitor1, p.indemnitor2].filter(Boolean);
    },
    caseDisplayId() {
      return this.caseData.case_fields?.court_case_number || '';
    },
    bondAmount() {
      return this.caseData.case_fields?.bond_amount_numeric || null;
    },
    court() {
      return this.caseData.case_fields?.court || '';
    },
  },

  methods: {
    displayName(p) {
      if (!p) return '';
      const id = p.identity || p;
      return [id.first_name || id.firstName, id.last_name || id.lastName]
        .filter(Boolean).join(' ');
    },

    formatCurrency(amount) {
      if (amount == null) return '';
      return amount.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    },

    formatTimeAgo(timestamp) {
      if (!timestamp) return '';
      const t = typeof timestamp === 'number'
        ? timestamp
        : new Date(timestamp).getTime();
      const diff = Date.now() - t;
      if (diff < 60 * 1000) return 'just now';
      if (diff < HOUR) return `${Math.floor(diff / (60 * 1000))}m ago`;
      if (diff < DAY) return `${Math.floor(diff / HOUR)}h ago`;
      if (diff < 2 * DAY) return 'yesterday';
      return `${Math.floor(diff / DAY)}d ago`;
    },

    onAction(actionType, payload = {}) {
      // All actions stubbed for this pass. Logs to console + alert so
      // we can verify wiring while the modal/dialogs catch up.
      console.log('[CaseDetail] action:', actionType, payload);
      const messages = {
        send_all_links:      'Send all magic links — coming next pass',
        generate_documents:  'Generate documents — coming next pass',
        seal_documents:      'Seal documents — coming next pass',
        archive:             'Archive case (Admin) — coming next pass',
        delete:              'Delete case (Admin) — coming next pass',
        add_indemnitor:      'Add indemnitor — coming next pass',
        send_link:           `Send magic link to ${payload.role} — coming next pass`,
        view_form:           `View ${payload.role}'s form — coming next pass`,
        ok_to_sign:          `Mark ${payload.role} OK-to-sign — coming next pass`,
        view_docs:           `View ${payload.role}'s signed documents — coming next pass`,
        remove_indemnitor:   'Remove indemnitor — coming next pass',
      };
      alert(messages[actionType] || `Action: ${actionType}`);
    },
  },
};
</script>


<style scoped>
.case-detail {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 64px); /* fill viewport below the app's top menu */
}

/* ── Sticky header ──────────────────────────────────────────── */
.case-header {
  background-color: #fff;
  border-bottom: 1px solid #e8e4dc;
  position: sticky;
  top: 0;
  z-index: 5;
}
.case-header-inner {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 24px;
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
}

.case-header-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1 1 0;
  min-width: 0;
}
.back-btn {
  text-transform: none;
  letter-spacing: 0;
  color: #635347;
}
.case-header-divider {
  width: 1px;
  height: 32px;
  background-color: #e8e4dc;
}
.case-header-info {
  flex: 1 1 0;
  min-width: 0;
}
.case-header-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.1em;
  flex-wrap: wrap;
}
.case-id {
  font-family: 'Courier New', monospace;
  font-weight: 600;
  color: #635347;
}
.case-id-missing {
  color: #aaa;
  font-family: inherit;
  font-style: italic;
  font-weight: normal;
}
.case-defendant {
  font-weight: 500;
  color: #222;
}
.case-bond {
  color: #555;
}
.case-header-sep {
  color: #ccc;
}
.case-header-subtitle {
  font-size: 12px;
  color: #888;
  margin-top: 2px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.case-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.header-status-chip {
  font-weight: 500;
}

/* ── Scrollable body ────────────────────────────────────────── */
.case-body {
  flex: 1 1 0;
  overflow-y: auto;
  padding: 24px;
  max-width: 1400px;
  width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
}

/* ── Sections ───────────────────────────────────────────────── */
.case-section {
  margin-bottom: 32px;
}
.section-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 12px;
}
.section-title {
  font-size: 1.1em;
  font-weight: 500;
  color: #635347;
  margin: 0 0 12px 0;
}
.section-header .section-title {
  margin-bottom: 0;
}
.section-stub {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  background-color: #faf9f6;
  border: 1px dashed #e8e4dc;
  border-radius: 6px;
  color: #888;
  font-size: 13px;
  font-style: italic;
}

.notes-display {
  padding: 12px 16px;
  background-color: #faf9f6;
  border: 1px solid #e8e4dc;
  border-radius: 6px;
  font-size: 14px;
  color: #333;
  white-space: pre-wrap;
}

.empty-indemnitor {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 32px;
  border: 1px dashed #e8e4dc;
  border-radius: 6px;
  color: #888;
}
.empty-indemnitor-text {
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 4px;
}

/* ── Mobile ─────────────────────────────────────────────────── */
@media (max-width: 768px) {
  .case-header-inner {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
  .case-header-divider {
    display: none;
  }
  .case-header-right {
    justify-content: flex-end;
  }
  .case-body {
    padding: 16px;
  }
}
</style>
